// src/lib/loyaltyPotential.ts
// Helper murni "Potensi Poin" (mig 6640): poin yang AKAN didapat pembeli
// saat nota/barangnya lunas. Mapper hasil RPC `get_my_loyalty_potential` &
// `get_my_invoice_loyalty_points` + kalimat tampilan.
//
// ⚠️ Angka poinnya DIHITUNG DI SQL, bukan di sini. RPC memakai rumus dan
// predikat mesin award (`award_loyalty_points_for_invoice`) dan paritasnya
// dikunci assert mig 6640 — potensi sebuah nota = poin yang dikredit saat nota
// itu lunas. Berkas ini hanya mengoersi JSON secara defensif, mengelompokkan,
// dan menyusun kalimat. Jangan menambahkan hitungan poin di sini (bandingkan
// batas cermin `computeProgramPoints` di `loyalty.ts`, bughunt F3-7).
// TANPA dependensi Supabase → mudah diuji.

import { formatInvoiceNo, INVOICE_STATUS_META, type InvoiceStatus } from "@/lib/invoices";
import { formatPoints } from "@/lib/loyalty";
import { WIB_TIME_ZONE } from "@/lib/wib";

export type LoyaltyPotentialProgram = {
  programId: string;
  name: string;
  endsAt: string | null;
  /** Poin dari nota yang sudah terbit tapi belum lunas (pasti). */
  invoicedPoints: number;
  invoicedQty: number;
  /** Poin dari barang yang belum dibuat notanya. */
  uninvoicedPoints: number;
  uninvoicedQty: number;
  /**
   * false = PERKIRAAN: program berbasis nominal / kelipatan > 1 pcs dihitung
   * per nota oleh award, jadi hasilnya bergantung pada pemecahan notanya.
   */
  uninvoicedExact: boolean;
};

export type LoyaltyPotentialInvoice = {
  invoiceId: string;
  invoiceNo: number | null;
  invoiceCode: string | null;
  status: string;
  programId: string;
  programName: string;
  qty: number;
  points: number;
};

export type LoyaltyPotential = {
  totalPoints: number;
  programs: LoyaltyPotentialProgram[];
  invoices: LoyaltyPotentialInvoice[];
};

export const EMPTY_LOYALTY_POTENTIAL: LoyaltyPotential = {
  totalPoints: 0,
  programs: [],
  invoices: [],
};

export type InvoicePointsEntry = {
  programId: string | null;
  programName: string;
  endsAt: string | null;
  qty: number;
  points: number;
};

export type InvoiceLoyaltyPoints = {
  /** Status nota; null = nota bukan milik pembeli ini / tak ditemukan. */
  status: string | null;
  /** Poin yang SUDAH masuk dari nota ini (bersih earn − revoke). */
  earned: InvoicePointsEntry[];
  /** Poin yang AKAN masuk bila nota ini lunas (hanya nota aktif). */
  potential: InvoicePointsEntry[];
};

export const EMPTY_INVOICE_LOYALTY_POINTS: InvoiceLoyaltyPoints = {
  status: null,
  earned: [],
  potential: [],
};

// ── Koersi defensif ──────────────────────────────────────────────────────────

function int(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

function text(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function textOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function rows(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v)
    ? (v.filter((x) => x !== null && typeof x === "object") as Record<string, unknown>[])
    : [];
}

function obj(v: unknown): Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

/**
 * JSON `get_my_loyalty_potential` → `LoyaltyPotential`. Baris tanpa id atau
 * tanpa poin dibuang, dan `totalPoints` = Σ baris program yang TAMPIL — angka
 * di kartu selalu sama dengan jumlah rinciannya.
 */
export function mapLoyaltyPotential(raw: unknown): LoyaltyPotential {
  const o = obj(raw);
  const programs = rows(o.programs)
    .map((p) => ({
      programId: text(p.programId),
      name: text(p.name) || "Program poin",
      endsAt: textOrNull(p.endsAt),
      invoicedPoints: int(p.invoicedPoints),
      invoicedQty: int(p.invoicedQty),
      uninvoicedPoints: int(p.uninvoicedPoints),
      uninvoicedQty: int(p.uninvoicedQty),
      uninvoicedExact: p.uninvoicedExact !== false,
    }))
    .filter((p) => p.programId !== "" && p.invoicedPoints + p.uninvoicedPoints > 0);

  const invoices = rows(o.invoices)
    .map((i) => {
      const no = Number(i.invoiceNo);
      return {
        invoiceId: text(i.invoiceId),
        invoiceNo: Number.isFinite(no) && no > 0 ? Math.trunc(no) : null,
        invoiceCode: textOrNull(i.invoiceCode),
        status: text(i.status),
        programId: text(i.programId),
        programName: text(i.programName) || "Program poin",
        qty: int(i.qty),
        points: int(i.points),
      };
    })
    .filter((i) => i.invoiceId !== "" && i.points > 0);

  const totalPoints = programs.reduce(
    (sum, p) => sum + p.invoicedPoints + p.uninvoicedPoints,
    0,
  );
  return { totalPoints, programs, invoices };
}

function mapEntries(v: unknown): InvoicePointsEntry[] {
  return rows(v)
    .map((e) => ({
      programId: textOrNull(e.programId),
      programName: text(e.programName) || "Program poin",
      endsAt: textOrNull(e.endsAt),
      qty: int(e.qty),
      points: int(e.points),
    }))
    .filter((e) => e.points > 0);
}

/** JSON `get_my_invoice_loyalty_points` → `InvoiceLoyaltyPoints`. */
export function mapInvoiceLoyaltyPoints(raw: unknown): InvoiceLoyaltyPoints {
  const o = obj(raw);
  return {
    status: textOrNull(o.status),
    earned: mapEntries(o.earned),
    potential: mapEntries(o.potential),
  };
}

// ── Kalimat tampilan ─────────────────────────────────────────────────────────

/** Batas periode program dalam WIB, mis. "1 Apr 2027". null bila tanpa batas. */
export function formatDeadlineWIB(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString("id-ID", {
    timeZone: WIB_TIME_ZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Batas pelunasan program dalam WIB, lengkap dengan jam: "1 Apr 2027, 23:59 WIB".
 * Program berlaku SAMPAI `ends_at` (award: `ends_at >= now()`), dan editor
 * menyimpan akhir hari 23:59:59 WIB — "sebelum 1 Apr" membuat pembeli mengira
 * tanggal itu sudah terlambat (bughunt F7-5). Jam dari data, bukan dikarang.
 */
export function formatDeadlineWithTimeWIB(iso: string | null): string | null {
  const date = formatDeadlineWIB(iso);
  if (!date || !iso) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: WIB_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hh = parts.find((p) => p.type === "hour")?.value ?? "00";
  const mm = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${date}, ${hh}:${mm} WIB`;
}

/** "+4.000 poin". */
export function formatPlusPoints(points: number): string {
  return `+${formatPoints(points)}`;
}

/** Baris kecil di kartu poin / Akun: "+9.000 poin menunggu pelunasan". */
export function potentialSummaryLabel(totalPoints: number): string | null {
  return totalPoints > 0 ? `${formatPlusPoints(totalPoints)} menunggu pelunasan` : null;
}

/** "Nota TGH2609170005 · 4 pcs". */
export function potentialInvoiceLabel(inv: LoyaltyPotentialInvoice): string {
  return `Tagihan ${formatInvoiceNo(inv.invoiceNo, inv.invoiceCode)} · ${inv.qty} pcs`;
}

/** Label status nota aktif ("Belum dibayar", "Menunggu ACC", …). */
export function potentialInvoiceStatusLabel(status: string): string {
  return INVOICE_STATUS_META[status as InvoiceStatus]?.label ?? "";
}

export type PotentialProgramGroup = {
  program: LoyaltyPotentialProgram;
  deadline: string | null;
  totalPoints: number;
  invoices: LoyaltyPotentialInvoice[];
};

/** Kelompokkan rincian per program (urutan program dari RPC dipertahankan). */
export function groupPotentialByProgram(potential: LoyaltyPotential): PotentialProgramGroup[] {
  return potential.programs.map((program) => ({
    program,
    deadline: formatDeadlineWithTimeWIB(program.endsAt),
    totalPoints: program.invoicedPoints + program.uninvoicedPoints,
    invoices: potential.invoices.filter((i) => i.programId === program.programId),
  }));
}

// ── Admin: daftar poin pelanggan (mig 6670) ──────────────────────────────────

/** Urutan tab admin Poin Pelanggan — `points` = poin terbanyak (bawaan). */
export type CustomerPointsSort = "points" | "potential";

export const CUSTOMER_POINTS_SORT_OPTIONS: { value: CustomerPointsSort; label: string }[] = [
  { value: "points", label: "Poin terbanyak" },
  { value: "potential", label: "Potensi terbanyak" },
];

/**
 * Batas baris daftar admin Poin Pelanggan (server menerima 1..500, mig 6680).
 * Daftar yang penuh berarti MUNGKIN masih ada pelanggan lain — layar wajib
 * mengatakannya (bughunt F7-7), bukan diam-diam memotong.
 */
export const CUSTOMER_POINTS_LIMIT = 200;

/** Keterangan "200 teratas" bila daftar mencapai batas; null bila tidak. */
export function customerPointsLimitNote(
  count: number,
  limit: number = CUSTOMER_POINTS_LIMIT,
): string | null {
  return count >= limit
    ? `Menampilkan ${limit} teratas — cari nama / toko / no HP untuk pelanggan lain.`
    : null;
}

/** Satu baris RPC `admin_loyalty_customer_points`. Urutan dari server dipertahankan. */
export type CustomerPointsRow = {
  customerId: string;
  name: string;
  phone: string;
  points: number;
  lifetimePoints: number;
  /** Poin yang akan masuk saat barang keep/nota aktifnya lunas. */
  potentialPoints: number;
};

/** JSON `admin_loyalty_customer_points` → baris; baris tanpa id dibuang. */
export function mapCustomerPointsRows(raw: unknown): CustomerPointsRow[] {
  return rows(raw)
    .map((r) => ({
      customerId: text(r.customerId),
      name: text(r.name).trim() || "Pelanggan",
      phone: text(r.phone),
      points: int(r.points),
      lifetimePoints: int(r.lifetimePoints),
      potentialPoints: int(r.potentialPoints),
    }))
    .filter((r) => r.customerId !== "");
}

export type InvoicePointsBanner = {
  kind: "potential" | "earned";
  points: number;
  /** Kalimat utama, mis. "Lunasi nota ini paling lambat 1 Apr 2027, 23:59 WIB → +4.000 poin". */
  headline: string;
  /** Nama program, mis. "Program Reward Syawal 2027". */
  detail: string;
};

function programNames(entries: InvoicePointsEntry[]): string {
  const names = Array.from(new Set(entries.map((e) => e.programName)));
  return `Program ${names.join(", ")}`;
}

/**
 * Kotak poin di detail nota. Nota aktif yang punya potensi → ajakan melunasi
 * (batas = tanggal berakhir program TERDEKAT). Selain itu, nota yang sudah
 * memberi poin → "Nota ini memberimu …". Tidak keduanya → null (disembunyikan).
 */
export function invoicePointsBanner(data: InvoiceLoyaltyPoints): InvoicePointsBanner | null {
  if (data.potential.length > 0) {
    const points = data.potential.reduce((s, e) => s + e.points, 0);
    const soonest = data.potential
      .map((e) => e.endsAt)
      .filter((e): e is string => e !== null)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    const deadline = formatDeadlineWithTimeWIB(soonest ?? null);
    return {
      kind: "potential",
      points,
      headline: `Lunasi tagihan ini${deadline ? ` paling lambat ${deadline}` : ""} → ${formatPlusPoints(points)}`,
      detail: programNames(data.potential),
    };
  }
  if (data.earned.length > 0) {
    const points = data.earned.reduce((s, e) => s + e.points, 0);
    return {
      kind: "earned",
      points,
      headline: `Tagihan ini memberimu ${formatPoints(points)}`,
      detail: programNames(data.earned),
    };
  }
  return null;
}
